// ✅ THEME + UX FIXES (NO business/API logic changes)
// FIX 1: Fully-rounded tables even when horizontally scrollable (outer + inner clip)
// FIX 2: Ensure horizontal scroll works so "Refund Reason" column is reachable
// FIX 3: Consistent clickable-row hover for student list table
// ✅ Kept your theme exactly: Orange header, blue surfaces, zebra rows, rounded containers
// ❗ No API / business logic changed

import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  Autocomplete,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Checkbox,
  Grid,
  LinearProgress,
  CircularProgress,
  TableContainer,
} from "@mui/material";
import { API_URL, API_UPLOAD_URL } from "./config";
import TuneIcon from "@mui/icons-material/Tune";
import SearchIcon from "@mui/icons-material/Search";
import GroupsIcon from "@mui/icons-material/Groups";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PaymentsIcon from "@mui/icons-material/Payments";
import DiscountIcon from "@mui/icons-material/Discount";
import ReplayCircleFilledIcon from "@mui/icons-material/ReplayCircleFilled";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";

/* ------------------------- Theme helpers ------------------------- */
const THEME_ORANGE = "#F07828";
const THEME_ORANGE_2 = "#ed7f35ff";
const BG = "#F2F2F2";
const BORDER = "rgba(25,118,210,0.20)";
const BORDER_HOVER = "rgba(25,118,210,0.45)";
const BORDER_FOCUS = "rgba(240,120,40,0.80)";
const ROW_ALT = "rgba(25,118,210,0.06)";
const ROW_HOVER = "rgba(25,118,210,0.10)";

const pageWrapSx = { p: 2, backgroundColor: BG };
const rootPaperSx = {
  mx: "auto",
  borderRadius: 2,
  overflow: "hidden",
  border: "1px solid rgba(25,118,210,0.15)",
};
const headerSx = {
  px: 2,
  py: 1.5,
  background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
  color: "white",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 2,
  flexWrap: "wrap",
};
const bodySx = { p: 2, backgroundColor: BG };

const surfaceSx = {
  borderRadius: 2,
  backgroundColor: "white",
  border: `1px solid ${BORDER}`,
};

const sectionSx = {
  ...surfaceSx,
  p: 2,
  position: "relative",
  overflow: "hidden",
  "&:before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 6,
    background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    opacity: 0.95,
  },
};

const inputSx = {
  "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
};

const primaryBtnSx = {
  borderRadius: 2,
  textTransform: "none",
  fontWeight: 900,
  backgroundColor: THEME_ORANGE,
  "&:hover": { backgroundColor: THEME_ORANGE_2 },
};

const outlinedBtnSx = {
  borderRadius: 2,
  textTransform: "none",
  fontWeight: 900,
  borderColor: "rgba(25,118,210,0.35)",
  color: "rgba(25,118,210,0.95)",
  "&:hover": {
    borderColor: "rgba(25,118,210,0.55)",
    backgroundColor: "rgba(25,118,210,0.04)",
  },
};

const dangerBtnSx = {
  borderRadius: 2,
  textTransform: "none",
  fontWeight: 900,
  backgroundColor: "#ED1C24",
  "&:hover": { backgroundColor: "#D11A1C" },
};

const theadRowSx = {
  backgroundColor: "rgba(25,118,210,0.95)",
  "& th": {
    color: "#fff",
    fontWeight: 900,
    borderBottom: "1px solid rgba(255,255,255,0.18)",
    whiteSpace: "nowrap",
    py: 1,
  },
};

const sectionTitleSx = {
  fontWeight: 900,
  color: THEME_ORANGE,
  mb: 1,
  display: "flex",
  alignItems: "center",
  gap: 0.75,
};

const zebraRowSx = (idx, clickable = false) => ({
  backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
  cursor: clickable ? "pointer" : "default",
  "&:hover": { backgroundColor: clickable ? "rgba(240,120,40,0.06)" : ROW_HOVER },
  "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)", py: 0.8 },
});

const subtotalRowSx = {
  backgroundColor: "rgba(0,0,0,0.04)",
  "& td": { fontWeight: 900 },
};

/**
 * ✅ FIX: Fully-rounded tables even with horizontal scroll (same pattern as your "below theme")
 * - Outer Paper clips corners
 * - Inner Box provides overflowX: auto
 * - TableContainer uses component="div" to avoid radius conflicts
 */
const tableCardSx = {
  ...surfaceSx,
  p: 0,
  overflow: "hidden", // clips header corners
};

const scrollClipSx = {
  width: "100%",
  maxWidth: "100%",
  overflowX: "auto",
  overflowY: "hidden",
  borderRadius: 2,
  WebkitOverflowScrolling: "touch",
  "&::-webkit-scrollbar": {
    height: 8,
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(25,118,210,0.35)",
    borderRadius: 8,
  },
};

function loadScript(src) {
  return new Promise((resolve) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve(true);
    const s = document.createElement("script");
    s.src = src;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

/* ----------------------- End theme helpers ----------------------- */

const AdminFeeManagement = () => {
  const [fees, setFees] = useState([]);
  const [logincampusCode, setCampusCode] = useState("");
  const [username, setUsername] = useState("");
  const [studentId, setStudentId] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [payMsg, setPayMsg] = useState(null);     // {type, text}
  const [payBusy, setPayBusy] = useState(false);  // disables Pay Now while starting


  const [selectedFees, setSelectedFees] = useState([]);
  const [paymentAmounts, setPaymentAmounts] = useState({});
  const [discountAmounts, setDiscountAmounts] = useState({});
  const [discountReasons, setDiscountReasons] = useState({});
  const [paymentMethod, setPaymentMethod] = useState("");
  const [deletedReasons, setDeletedReasons] = useState({});
  const [refundAmounts, setRefundAmounts] = useState({});
  const [refundReasons, setRefundReasons] = useState({});

  const token = localStorage.getItem("auth");

  const [filters, setFilters] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    SemesterCode: [],
  });

  const [options, setOptions] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    SemesterCode: [],
  });

  const [loadingLookups, setLoadingLookups] = useState(false);

  const [filteredStudents, setFilteredStudents] = useState([]);
  const [allFilteredStudents, setAllFilteredStudents] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState("");
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    if (token) {
      try {
        const decodedToken = JSON.parse(atob(token.split(".")[1]));
        setCampusCode(decodedToken.campusCode || decodedToken.CampusCode || "");
        setUsername(decodedToken.username || decodedToken.Username || "");
      } catch (err) {
        console.error("Error decoding token:", err);
        setError("Failed to decode authentication token.");
      }
    }
  }, [token]);

  useEffect(() => {
    if (!logincampusCode) return;
    let cancelled = false;

    (async () => {
      try {
        setLoadingLookups(true);
        const { data } = await axios.get(`${API_URL}/api/lookups/all`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { CampusCode: logincampusCode },
        });

        if (cancelled) return;
        setOptions({
          SubCampusCode: data?.SubCampusCode || [],
          DegreeCode: data?.DegreeCode || [],
          BranchCode: data?.BranchCode || [],
          SemesterCode: (data?.SemesterCode || []).map(String),
        });
      } catch (e) {
        console.error("Failed to load dropdowns:", e);
      } finally {
        if (!cancelled) setLoadingLookups(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [logincampusCode, token]);

  const fetchFees = async (idParam) => {
    const sid = idParam || studentId;

    if (!sid || !logincampusCode || !username) {
      setError("Please enter a valid Student ID.");
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/students/${sid}/adminfees`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFees(response.data || []);
      setSelectedFees([]);
      setPaymentAmounts({});
      setError("");
      setStudentId(sid);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch fees for the student.");
    }
  };

  const handleFeeSelection = (fee) => {
    setSelectedFees((prev) => {
      if (prev.some((selected) => selected.StudentFeeID === fee.StudentFeeID)) {
        setPaymentAmounts((prevPayments) => {
          const newPayments = { ...prevPayments };
          delete newPayments[fee.StudentFeeID];
          return newPayments;
        });
        return prev.filter((selected) => selected.StudentFeeID !== fee.StudentFeeID);
      } else {
        const feeDue = Number(fee.FeeDue || 0);

        setPaymentAmounts((prevPayments) => ({
          ...prevPayments,
          [fee.StudentFeeID]: feeDue > 0 ? feeDue : "",
        }));

        return [
          ...prev,
          {
            StudentFeeID: fee.StudentFeeID,
            StudentKey: fee.StudentKey,
            FeeStructureID: fee.FeeStructureID,
            Semester: fee.Semester,
          },
        ];
      }
    });
  };

  const handlePaymentAmountChange = (StudentFeeID, value) => {
    const enteredAmount = parseFloat(value) || 0;
    const feeEntry = fees.find((fee) => fee.StudentFeeID === StudentFeeID);
    if (!feeEntry) {
      setError("Invalid Fee Selection");
      return;
    }

    if (enteredAmount < 0) {
      setError(`Payment amount cannot be negative.`);
      return;
    }

    const feeDue = Number(feeEntry.FeeDue || 0);

    if (enteredAmount > feeDue) {
      setError(`Payment amount cannot exceed Fee Due (₹${feeDue})`);
      return;
    }

    setError("");
    setPaymentAmounts((prev) => ({ ...prev, [StudentFeeID]: enteredAmount }));
  };

  const handlePayment = async () => {
  try {
    setError("");
    setPayMsg(null);

    if (selectedFees.length === 0) {
      setError("Select at least one fee line.");
      return;
    }

    if (!paymentMethod) {
      setError("Select a payment method.");
      return;
    }

    if (paymentMethod !== "Online") {
      setError("For Cash/Card/Bank Transfer use your normal offline flow. Select 'Online' for gateway checkout.");
      return;
    }

    const Items = selectedFees.map((fee) => ({
      StudentFeeID: fee.StudentFeeID,
      Amount: Number(paymentAmounts[fee.StudentFeeID] || 0),
    }));

    if (Items.some((x) => !(x.Amount > 0))) {
      setError("Each selected fee must have a valid payment amount (> 0).");
      return;
    }

    const total = Items.reduce((a, b) => a + Number(b.Amount || 0), 0);

    if (!(total > 0)) {
      setError("Total must be > 0.");
      return;
    }

    setPayBusy(true);
    setPayMsg({ type: "info", text: `Starting online payment for ₹${total}...` });

    const idem = `CART_${studentId}_${Date.now()}`;

    const { data } = await axios.post(
      `${API_URL}/api/payments/student/create-cart-order`,
      {
        Mode: "LIVE",
        IdempotencyKey: idem,
        Items,
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    // ✅ External Redirect / Bank URL / BillDesk Collect URL
    if (data.Gateway === "EXTERNAL_REDIRECT") {
      const redirectUrl = data.RedirectUrl || data.ExternalRedirectUrl;
    
      if (!redirectUrl) {
        throw new Error("Redirect URL is not configured for this gateway.");
      }
    
      if (!/^https:\/\//i.test(String(redirectUrl))) {
        throw new Error("Invalid redirect URL. Only https:// URLs are allowed.");
      }
    
      setPayMsg({
        type: "info",
        text: "Opening payment page...",
      });
    
      // ✅ Open in new tab
      const win = window.open(
        redirectUrl,
        "_blank",
        "noopener,noreferrer"
      );
    
      if (!win) {
        throw new Error(
          "Popup blocked by browser. Please allow popups for this site."
        );
      }
    
      setPayMsg({
        type: "success",
        text: "Payment page opened in a new tab.",
      });
    
      return;
    }

    // ---------- Razorpay ----------
    if (data.Gateway === "RAZORPAY") {
      const ok = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
      if (!ok) throw new Error("Failed to load Razorpay");

      const rzp = new window.Razorpay({
        key: data.RazorpayKeyId,
        amount: Math.round(Number(data.Amount) * 100),
        currency: data.Currency || "INR",
        name: `${logincampusCode} Fees` || "Fees",
        description: data.ClientTxnRef,
        order_id: data.RazorpayOrderId,
        handler: async (resp) => {
          try {
            await axios.post(
              `${API_URL}/api/payments/student/confirm-cart`,
              {
                CartId: data.CartId,
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );

            setPayMsg({
              type: "success",
              text: "Payment received. Updating fee status...",
            });

            await fetchFees(studentId);
            setSuccess(true);
          } catch (e) {
            console.error("confirm-cart failed", e);
            setPayMsg({
              type: "error",
              text: e?.response?.data?.error || "Payment confirmed but server update failed.",
            });
          }
        },
        modal: {
          ondismiss: () => {
            setPayMsg({ type: "warning", text: "Payment window closed." });
          },
        },
      });

      rzp.open();
      return;
    }

    // ---------- Paytm ----------
    if (data.Gateway === "PAYTM") {
      const ok = await loadScript(
        "https://securegw-stage.paytm.in/merchantpgpui/checkoutjs/merchants/" +
          data.PaytmMid +
          ".js"
      );

      if (!ok) throw new Error("Failed to load Paytm CheckoutJS");

      const config = {
        root: "",
        flow: "DEFAULT",
        data: {
          orderId: data.PaytmOrderId,
          token: data.PaytmTxnToken,
          tokenType: "TXN_TOKEN",
          amount: String(data.Amount),
        },
        handler: {
          notifyMerchant: function (eventName) {
            if (eventName === "SESSION_EXPIRED") {
              setPayMsg({ type: "error", text: "Paytm session expired. Retry." });
            }
          },
        },
      };

      setPayMsg({ type: "info", text: "Opening Paytm..." });
      await window.Paytm.CheckoutJS.init(config);
      window.Paytm.CheckoutJS.invoke();

      return;
    }

    throw new Error("Unsupported gateway from server");
  } catch (err) {
    console.error(err);
    setPayMsg(null);
    setError(err?.response?.data?.error || err.message || "Payment failed");
  } finally {
    setPayBusy(false);
  }
};


  const handleRefund = async () => {
    if (selectedFees.length === 0 || !paymentMethod) {
      setError("Select at least one fee and a refund method.");
      return;
    }

    const feesToRefund = selectedFees.map((fee) => ({
      StudentFeeID: fee.StudentFeeID,
      StudentKey: fee.StudentKey,
      FeeStructureID: fee.FeeStructureID,
      RefundAmount: refundAmounts[fee.StudentFeeID] || 0,
      RefundReason: refundReasons[fee.StudentFeeID] || "",
    }));

    if (feesToRefund.some((fee) => !fee.RefundReason.trim())) {
      setError("Each selected fee must have a valid refund amount and reason.");
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/students/${studentId}/refunds`,
        { Fees: feesToRefund, RefundMethod: paymentMethod, Username: username },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(true);
      fetchFees(studentId);
    } catch (err) {
      console.error(err);
      setError("Refund processing failed.");
    }
  };

  const handleDiscount = async () => {
    if (selectedFees.length === 0) {
      setError("Select at least one fee and provide discount details.");
      return;
    }

    const feesToDiscount = selectedFees.map((fee) => ({
      StudentFeeID: fee.StudentFeeID,
      StudentKey: fee.StudentKey,
      FeeStructureID: fee.FeeStructureID,
      DiscountAmount: discountAmounts[fee.StudentFeeID] || 0,
      DiscountReason: discountReasons[fee.StudentFeeID] || "",
    }));

    if (feesToDiscount.some((fee) => !fee.DiscountReason.trim())) {
      setError("Each selected fee must have a valid discount amount and reason.");
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/students/${studentId}/discounts`,
        { Fees: feesToDiscount, Username: username },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(true);
      fetchFees(studentId);
    } catch (err) {
      console.error(err);
      setError("Discount processing failed.");
    }
  };

  const handleDeleteFee = async (StudentFeeID) => {
    if (!studentId) {
      setError("Student ID is required.");
      return;
    }

    const reason = deletedReasons[StudentFeeID] || "";
    if (!reason.trim()) {
      setError("Please provide a reason before deleting.");
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/students/${studentId}/fees/${StudentFeeID}`, {
        params: { Username: username, DeletedReason: reason },
        headers: { Authorization: `Bearer ${token}` },
      });

      setSuccess(true);
      fetchFees(studentId);
    } catch (err) {
      console.error(err);
      setError("Failed to delete fee entry.");
    }
  };

  const getSubtotal = (field) => fees.reduce((acc, fee) => acc + Number(fee[field] || 0), 0);
  const paymentSubtotal = Object.values(paymentAmounts).reduce((acc, val) => acc + Number(val || 0), 0);
  const refundSubtotal = Object.values(refundAmounts).reduce((acc, val) => acc + Number(val || 0), 0);
  const discountSubtotal = Object.values(discountAmounts).reduce((acc, val) => acc + Number(val || 0), 0);
  const feeDueSubtotal = getSubtotal("FeeDue");

  const anyFilterSelected =
    (filters.SubCampusCode?.length || 0) > 0 ||
    (filters.DegreeCode?.length || 0) > 0 ||
    (filters.BranchCode?.length || 0) > 0 ||
    (filters.SemesterCode?.length || 0) > 0;

  const handleFilterStudents = async () => {
    setListError("");
    setFilteredStudents([]);
    setAllFilteredStudents([]);
    setIsCollapsed(false);

    if (!logincampusCode) return;
    if (!anyFilterSelected) {
      setListError("Select at least one dropdown filter to load students.");
      return;
    }

    try {
      setLoadingList(true);
      const params = { CampusCode: logincampusCode };
      if (filters.SubCampusCode.length) {params.SubCampusCode = filters.SubCampusCode.join(",");}
      if (filters.DegreeCode.length) {params.DegreeCode = filters.DegreeCode.join(",");}
      if (filters.BranchCode.length) {params.BranchCode = filters.BranchCode.join(",");}
      if (filters.SemesterCode.length) {params.SemesterCode = filters.SemesterCode.join(",");}

      const { data } = await axios.get(`${API_URL}/api/dataReportStudentFeeList`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      const arr = Array.isArray(data) ? data : [];
      setFilteredStudents(arr);
      setAllFilteredStudents(arr);
      if (!arr.length) setListError("No students found for the selected filters.");
    } catch (e) {
      console.error(e);
      setListError("Failed to load students. Please try again.");
    } finally {
      setLoadingList(false);
    }
  };

  const handleStudentRowClick = (s) => {
    if (isCollapsed && filteredStudents.length === 1 && filteredStudents[0].StudentID === s.StudentID) {
      setFilteredStudents(allFilteredStudents);
      setIsCollapsed(false);
      return;
    }

    setFilteredStudents([s]);
    setIsCollapsed(true);
    setStudentId(s.StudentID);
    fetchFees(s.StudentID);
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Student Fee Adjustment
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Filter students, load fee details, process payment/discount/refund
            </Typography>
          </Box>
        </Box>

        <Box sx={bodySx}>
          {error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                mb: 2,
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
              }}
            >
              {error}
            </Alert>
          )}

          {payMsg && (
            <Alert
              severity={payMsg.type}
              onClose={() => setPayMsg(null)}
              sx={{ mb: 2, borderRadius: 2 }}
            >
              {payMsg.text}
            </Alert>
          )}

          {success && (
            <Snackbar open autoHideDuration={3000} onClose={() => setSuccess(false)}>
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                Operation successful!
              </Alert>
            </Snackbar>
          )}

          {/* ✅ Filters */}
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={sectionTitleSx}>
                <TuneIcon fontSize="small" /> Student Filters
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(5, 1fr)" },
                  gap: 1,
                  textAlign: "left",
                }}
              >
                <Autocomplete
                  multiple
                  options={options.SubCampusCode || []}
                  value={filters.SubCampusCode || []}
                  onChange={(_, value) => setFilters((p) => ({ ...p, SubCampusCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="SubCampusCode(s)"
                      size="small"
                      fullWidth
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  options={options.DegreeCode || []}
                  value={filters.DegreeCode || []}
                  onChange={(_, value) => setFilters((p) => ({ ...p, DegreeCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="DegreeCode(s)"
                      size="small"
                      fullWidth
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  options={options.BranchCode || []}
                  value={filters.BranchCode || []}
                  onChange={(_, value) => setFilters((p) => ({ ...p, BranchCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="BranchCode(s)"
                      size="small"
                      fullWidth
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  options={(options.SemesterCode || []).map(String)}
                  value={(filters.SemesterCode || []).map(String)}
                  onChange={(_, value) => setFilters((p) => ({ ...p, SemesterCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="SemesterCode(s)"
                      size="small"
                      fullWidth
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  <Button
                    variant="contained"
                    startIcon={<GroupsIcon />}
                    sx={{ ...primaryBtnSx, height: 36, fontSize: "0.80rem" }}
                    onClick={handleFilterStudents}
                    disabled={loadingLookups || loadingList}
                  >
                    {loadingList ? "Loading…" : "Filter Students"}
                  </Button>
                  {listError && (
                    <Typography variant="body2" sx={{ color: "error.main" }}>
                      {listError}
                    </Typography>
                  )}
                </Box>
              </Box>

              {loadingList && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress />
                </Box>
              )}
            </Box>
          </Paper>

          {/* ✅ Manual Student Search */}
          <Paper variant="outlined" sx={{ ...sectionSx, mt: 2 }}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={sectionTitleSx}>
                <SearchIcon fontSize="small" /> Manual Student Search
              </Typography>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                <TextField
                  label="Enter Student ID"
                  value={studentId}
                  onChange={(e) => setStudentId(e.target.value)}
                  sx={{ ...inputSx, flex: 1, minWidth: 260 }}
                  size="small"
                />
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  sx={{ ...primaryBtnSx, height: 36, minWidth: 110 }}
                  onClick={() => fetchFees()}
                >
                  Search
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* ✅ Students list table */}
          {filteredStudents.length > 0 && (
            <Paper variant="outlined" sx={{ ...sectionSx, mt: 2 }}>
              <Box sx={{ pl: 1 }}>
                <Typography sx={sectionTitleSx}>
                  <GroupsIcon fontSize="small" /> Students Matching Filters
                </Typography>

                {/* ✅ Rounded + scroll-safe table wrapper */}
                <Paper elevation={0} sx={tableCardSx}>
                  <Box sx={scrollClipSx}>
                    <TableContainer component="div">
                      <Table size="small" sx={{ minWidth: 900 }}>
                        <TableHead>
                          <TableRow sx={theadRowSx}>
                            <TableCell sx={{ width: 60 }}>#</TableCell>
                            <TableCell>StudentID</TableCell>
                            <TableCell>Full Name</TableCell>
                            <TableCell>SubCampus</TableCell>
                            <TableCell>Degree</TableCell>
                            <TableCell>Branch</TableCell>
                            <TableCell>Semester</TableCell>
                            <TableCell>FeeDue</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredStudents.map((s, idx) => (
                            <TableRow
                              key={`${s.StudentID}-${idx}`}
                              hover
                              sx={zebraRowSx(idx, true)}
                              onClick={() => handleStudentRowClick(s)}
                            >
                              <TableCell>{idx + 1}</TableCell>
                              <TableCell>{s.StudentID}</TableCell>
                              <TableCell>{s.FullName}</TableCell>
                              <TableCell>{s.SubCampusCode}</TableCell>
                              <TableCell>{s.DegreeCode}</TableCell>
                              <TableCell>{s.BranchCode}</TableCell>
                              <TableCell>{s.SemesterCode}</TableCell>
                              <TableCell>{s.FeeDue}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Paper>

                <Typography sx={{ mt: 1, fontSize: "0.8rem", opacity: 0.75 }}>
                  Tip: Click a student row to collapse to that student and load fees. Click again to expand the full list.
                </Typography>
              </Box>
            </Paper>
          )}

          {/* ✅ Fee Details */}
          {fees.length > 0 && (
            <Paper variant="outlined" sx={{ ...sectionSx, mt: 2 }}>
              <Box sx={{ pl: 1 }}>
                <Typography sx={sectionTitleSx}>
                  <ReceiptLongIcon fontSize="small" /> Fee Details
                </Typography>

                {/* ✅ Rounded + horizontal-scrollable table (Refund Reason will be reachable) */}
                <Paper elevation={0} sx={tableCardSx}>
                  <Box sx={scrollClipSx}>
                    <TableContainer component="div">
                      <Table size="small" sx={{ minWidth: 1700 }}>
                        <TableHead>
                          <TableRow sx={theadRowSx}>
                            <TableCell>Select</TableCell>
                            <TableCell>#</TableCell>
                            <TableCell>Fee Type</TableCell>
                            <TableCell>Fee</TableCell>
                            <TableCell>Fee Paid</TableCell>
                            <TableCell>Discount</TableCell>
                            <TableCell>Refund</TableCell>
                            <TableCell>Fee Due</TableCell>
                            <TableCell>Payment Amount</TableCell>
                            <TableCell>Discount Amount</TableCell>
                            <TableCell>Refund Amount</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Discount Reason</TableCell>
                            <TableCell>Refund Reason</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {fees.map((fee, index) => (
                            <TableRow key={fee.StudentFeeID} sx={zebraRowSx(index)}>
                              <TableCell>
                                <Checkbox
                                  checked={selectedFees.some((s) => s.StudentFeeID === fee.StudentFeeID)}
                                  onChange={() => handleFeeSelection(fee)}
                                />
                              </TableCell>

                              <TableCell>{index + 1}</TableCell>
                              <TableCell>
                                {fee.Semester}-{fee.FeeType}
                              </TableCell>
                              <TableCell>{fee.FeeAmount}</TableCell>
                              <TableCell>{fee.FeePaid}</TableCell>
                              <TableCell>{fee.DiscountAmount}</TableCell>
                              <TableCell>{fee.RefundAmount}</TableCell>
                              <TableCell>{fee.FeeDue}</TableCell>

                              <TableCell>
                                <TextField
                                  size="small"
                                  type="number"
                                  sx={{ ...inputSx, minWidth: 110 }}
                                  inputProps={{
                                    min: 1,
                                    max:
                                      Number(fee.FeeAmount || 0) -
                                      Number(fee.FeePaid || 0) -
                                      Number(fee.DiscountAmount || 0) -
                                      Number(fee.RefundAmount || 0),
                                  }}
                                  value={paymentAmounts[fee.StudentFeeID] || ""}
                                  onChange={(e) =>
                                    handlePaymentAmountChange(fee.StudentFeeID, e.target.value)
                                  }
                                />
                              </TableCell>

                              <TableCell>
                                <TextField
                                  size="small"
                                  type="number"
                                  sx={{ ...inputSx, minWidth: 110 }}
                                  inputProps={{
                                    min: 0,
                                    max: Math.max(
                                      0,
                                      Number(fee.FeeAmount || 0) -
                                        Number(fee.DiscountAmount || 0) -
                                        Number(fee.RefundAmount || 0)
                                    ),
                                  }}
                                  value={discountAmounts[fee.StudentFeeID] || ""}
                                  onChange={(e) => {
                                    const enteredAmount = parseFloat(e.target.value) || 0;
                                    const maxDiscount = Math.max(
                                      0,
                                      Number(fee.FeeAmount || 0) -
                                        Number(fee.DiscountAmount || 0) -
                                        Number(fee.RefundAmount || 0)
                                    );
                                    if (enteredAmount > maxDiscount) {
                                      setError(`Discount amount cannot exceed ₹${maxDiscount}.`);
                                      return;
                                    }
                                    setError("");
                                    setDiscountAmounts((prev) => ({
                                      ...prev,
                                      [fee.StudentFeeID]: enteredAmount,
                                    }));
                                  }}
                                />
                              </TableCell>

                              <TableCell>
                                <TextField
                                  size="small"
                                  type="number"
                                  sx={{ ...inputSx, minWidth: 110 }}
                                  inputProps={{
                                    max: Math.max(0, Number(fee.FeePaid || 0)),
                                  }}
                                  value={refundAmounts[fee.StudentFeeID] || ""}
                                  onChange={(e) => {
                                    const enteredAmount = parseFloat(e.target.value) || 0;
                                    const maxRefundable = Math.max(
                                      0,
                                      Number(fee.FeePaid || 0) + Number(fee.RefundAmount || 0)
                                    );
                                    if (enteredAmount < 0) {
                                      setError("Refund amount cannot be negative.");
                                      return;
                                    }
                                    if (enteredAmount > maxRefundable) {
                                      setError(`Refund amount cannot exceed ₹${maxRefundable}.`);
                                      return;
                                    }
                                    setError("");
                                    setRefundAmounts((prev) => ({
                                      ...prev,
                                      [fee.StudentFeeID]: enteredAmount,
                                    }));
                                  }}
                                />
                              </TableCell>

                              <TableCell>{fee.Status}</TableCell>

                              <TableCell>
                                <TextField
                                  size="small"
                                  sx={{ ...inputSx, minWidth: 220 }}
                                  value={discountReasons[fee.StudentFeeID] || ""}
                                  onChange={(e) =>
                                    setDiscountReasons((prev) => ({
                                      ...prev,
                                      [fee.StudentFeeID]: e.target.value,
                                    }))
                                  }
                                />
                              </TableCell>

                              <TableCell>
                                <TextField
                                  size="small"
                                  sx={{ ...inputSx, minWidth: 220 }}
                                  value={refundReasons[fee.StudentFeeID] || ""}
                                  onChange={(e) =>
                                    setRefundReasons((prev) => ({
                                      ...prev,
                                      [fee.StudentFeeID]: e.target.value,
                                    }))
                                  }
                                />
                              </TableCell>
                            </TableRow>
                          ))}

                          <TableRow sx={subtotalRowSx}>
                            <TableCell colSpan={7} sx={{ textAlign: "left" }}>
                              Subtotal
                            </TableCell>
                            <TableCell sx={{ color: "#D32F2F" }}>₹{feeDueSubtotal}</TableCell>
                            <TableCell sx={{ color: "rgba(25,118,210,0.95)" }}>
                              ₹{paymentSubtotal}
                            </TableCell>
                            <TableCell sx={{ color: THEME_ORANGE }}>₹{discountSubtotal}</TableCell>
                            <TableCell colSpan={4} sx={{ color: "#D11A1C" }}>
                              ₹{refundSubtotal}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Paper>

                {/* ✅ Actions */}
                <Paper variant="outlined" sx={{ ...surfaceSx, mt: 2, p: 2 }}>
                  <Typography sx={sectionTitleSx}>
                    <PaymentsIcon fontSize="small" /> Payment & Refund Actions
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="pay-method">Payment / Refund Method</InputLabel>
                        <Select
                          labelId="pay-method"
                          label="Payment / Refund Method"
                          value={paymentMethod}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          sx={inputSx}
                        >
                          <MenuItem value="Cash">Cash</MenuItem>
                          <MenuItem value="Online">Online</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<PaymentsIcon />}
                        sx={primaryBtnSx}
                        onClick={handlePayment}
                        disabled={payBusy || selectedFees.length === 0 || paymentMethod !== "Online"}
                      >
                        {payBusy ? "Starting..." : "Pay Now"}
                      </Button>

                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<DiscountIcon />}
                        sx={primaryBtnSx}
                        onClick={handleDiscount}
                        disabled
                      >
                        Process Discount
                      </Button>
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<ReplayCircleFilledIcon />}
                        sx={dangerBtnSx}
                        onClick={handleRefund}
                        disabled
                      >
                        Process Refund
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
            </Paper>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminFeeManagement;
